<script setup>
// The admin "Joining" card on every entity page (JOIN_SETTINGS_PLAN.md §7.3): one line
// that says how joining behaves, the link with copy/share/PDF, the waiting/requests
// counts, and a button into the full join settings dialog. Nothing is edited inline.
// Parent gates rendering on admin. Replaces InviteMembersCta / EntityInviteLinksDialog.
import TlIconBtn from '~/components/admin/TlIconBtn.vue';
import ShortLinkShareWrapper from '~/components/share/ShortLinkShareWrapper.vue';
import JoinSettingsDialog from '~/components/join/JoinSettingsDialog.vue';
import {useJoinSettings} from '~/composables/useJoinSettings';

const props = defineProps({
  referenceType: {type: Number, required: true},
  referenceId: {type: String, required: true},
  clubId: {type: String, required: true},
  entityName: {type: String, default: ''},
  showTournamentWarning: {type: Boolean, default: false},
});
// The waiting-area and join-request counts used to sit here as pills, but each
// one duplicated a card rendered directly beneath this one on every page.
const emit = defineEmits(['changed']);

const {settings, pending, errors, load, url, copyUrl, downloadPdf, status, summary} =
    useJoinSettings(() => props.referenceType, () => props.referenceId);
const {setAlertMessage} = useAlertService();

const dialog = ref(false);
const enabled = computed(() => settings.value?.enabled !== false);

watch(() => props.referenceId, () => load(), {immediate: true});

const onCopy = async () => {
  if (await copyUrl()) setAlertMessage('Join link copied to clipboard');
};

const onDialogChanged = async () => {
  await load();
  emit('changed');
};

const statusClass = computed(() => ({
  'tl-chip--ok': status.value?.key === 'active',
  'tl-chip--warn': status.value?.key === 'expired' || status.value?.key === 'usedUp' || status.value?.key === 'off',
}));
</script>

<template>
  <div class="tl-card jsc-card">
    <div class="jsc-head">
      <div class="jsc-head-ic" :class="{'jsc-head-ic--off': settings && !enabled}">
        <v-icon size="18" :color="settings && !enabled ? 'textSubtle' : 'primary'">
          {{ settings && !enabled ? 'mdi-cancel' : 'mdi-account-multiple-plus' }}
        </v-icon>
      </div>
      <div class="flex-fill min-w-0">
        <div class="jsc-title-row">
          <span class="jsc-title">Joining</span>
          <v-chip v-if="status" variant="flat" size="x-small" class="tl-chip" :class="statusClass">{{ status.label }}</v-chip>
        </div>
        <div class="jsc-summary">
          <template v-if="settings">{{ summary }}</template>
          <template v-else-if="pending">Loading join settings…</template>
          <template v-else>Join settings could not be loaded.</template>
        </div>
      </div>
      <v-btn color="primary" variant="tonal" size="small" rounded="pill" prepend-icon="mdi-tune-variant"
             :disabled="!settings" @click="dialog = true">
        <span class="d-none d-sm-inline">Join settings</span><span class="d-sm-none">Settings</span>
      </v-btn>
    </div>

    <!-- The link: tap to copy, or share / print it -->
    <div v-if="settings && enabled" class="jsc-link">
      <button type="button" class="jsc-url" @click="onCopy">
        <v-icon size="15" icon="mdi-link-variant" class="mr-1"/>{{ url }}
      </button>
      <div class="jsc-link-actions">
        <tl-icon-btn tooltip="Copy link" icon="mdi-content-copy" @click="onCopy"/>
        <short-link-share-wrapper :target-path="'/invite/' + settings.link.slug">
          <template #default="{click}">
            <tl-icon-btn tooltip="Share" icon="mdi-share-variant" @click="click"/>
          </template>
        </short-link-share-wrapper>
        <tl-icon-btn tooltip="Printable poster (PDF)" icon="mdi-file-pdf-box" @click="downloadPdf"/>
      </div>
    </div>
    <v-skeleton-loader v-else-if="pending && !settings" type="text" class="mx-3"/>
    <crud-error-display v-else-if="!settings" class="mx-3 mb-3" :errors="errors"/>

    <join-settings-dialog
        v-model="dialog"
        :reference-type="referenceType"
        :reference-id="referenceId"
        :entity-name="entityName"
        :show-tournament-warning="showTournamentWarning"
        @changed="onDialogChanged"
    />
  </div>
</template>

<style scoped lang="scss">
.jsc-card {
  overflow: hidden;
  padding-bottom: 12px;
}

.jsc-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: rgb(var(--v-theme-brandTint));
}

.jsc-head-ic {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: rgb(var(--v-theme-surface));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.jsc-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.jsc-title {
  font-size: 13.5px;
  font-weight: 700;
}

.jsc-summary {
  font-size: 12px;
  color: rgb(var(--v-theme-charcoal));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.jsc-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px 0;
}

.jsc-url {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  text-align: left;
  font-size: 12.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: rgb(var(--v-theme-light));
  border: 1px dashed rgb(var(--v-theme-border));
  border-radius: 8px;
  padding: 7px 10px;
  cursor: copy;

  :deep(.v-icon) {
    flex-shrink: 0;
  }
}

.jsc-link-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}

</style>
